import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Icons } from '@/components/ui/icons';
import { NewServiceModal, type NewServiceInitialData } from '@/components/features/new-service-modal';
import { ActivityFeed } from '@/components/features/provider/ActivityFeed';
import { ProfileCompletionBanner } from '@/components/features/profile-completion-banner';
import { cn } from '@/lib/utils';
import { type ProviderDashboardStats, type UserWithProfile, type DashboardDuty } from '@/types/dashboard';
import { type Activity } from '@/types/applications';
import type { Id } from '../../convex/_generated/dataModel';
import { getDurationBucket, trackServiceCreated, trackServiceCreationFailed } from '@/lib/analytics';
import { formatBerlinDateWithOptions, formatBerlinTime, getBerlinDateParts, isBerlinToday } from '@/lib/berlin-time';

/** How close a duty must be (in ms) to show urgency styling */
const URGENT_THRESHOLD_MS = 48 * 60 * 60 * 1000; // 48 hours
const IMMINENT_THRESHOLD_MS = 12 * 60 * 60 * 1000; // 12 hours

const ProviderDashboardPage = (): React.ReactElement => {
  const { t, i18n } = useTranslation('dashboard');

  // Core queries with proper typing
  const allMine = (useQuery(api.app.duties.queries.listMyOwnedDuties, {}) as DashboardDuty[] | undefined) ?? [];
  const me = useQuery(api.app.users.queries.getMe, {}) as UserWithProfile | null | undefined;
  const stats = useQuery(api.app.providerDashboard.getProviderDashboardStats, {}) as ProviderDashboardStats | undefined;
  const activities = useQuery(api.app.providerDashboard.getProviderActivities, { limit: 10 }) as Activity[] | undefined;
  const marketingDrafts = useQuery(api.app.providerServiceDrafts.listMyActiveProviderServiceDrafts, {}) || [];

  // Upcoming duties (first 6 for full-width layout)
  const now = Date.now();
  const upcoming = allMine
    .filter((d) => (d.startDate || 0) >= now && d.status !== 'cancelled')
    .slice()
    .sort((a, b) => (a.startDate || 0) - (b.startDate || 0))
    .slice(0, 6);

  // Modal and mutations
  const createDuty = useMutation(api.app.duties.mutations.createDuty);
  const consumeProviderServiceDraft = useMutation(api.app.providerServiceDrafts.consumeProviderServiceDraft);
  const [openNew, setOpenNew] = React.useState(false);

  const [initialServiceData, setInitialServiceData] = React.useState<NewServiceInitialData | undefined>(undefined);

  const { draftId: highlightedDraftId } = Route.useSearch();

  const handleModalOpenChange = React.useCallback((open: boolean) => {
    setOpenNew(open);
    if (!open) {
      setInitialServiceData(undefined);
    }
  }, []);

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const dateTimeFormatter = React.useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'Europe/Berlin',
      }),
    [locale]
  );

  const highlightedDraft = React.useMemo(() => {
    if (highlightedDraftId) {
      const matchingDraft = marketingDrafts.find((draft: { draftId: string }) => String(draft.draftId) === highlightedDraftId);
      if (matchingDraft) {
        return matchingDraft;
      }
    }

    return marketingDrafts[0];
  }, [highlightedDraftId, marketingDrafts]);

  const openDraftInModal = React.useCallback(
    (draft: {
      draftId: string;
      sourceKey: string;
      serviceType: NewServiceInitialData['serviceType'];
      startDate: string;
      endDate: string;
      serviceAreaId?: string;
      manualServiceArea?: string;
      manualBereitschaftspraxis?: string;
      manualFahrdienstgebiet?: string;
      compensationType?: NewServiceInitialData['compensationType'];
      fixedPrice?: number;
      minPrice?: number;
      maxPrice?: number;
      hasNotfallscheinbeteiligung: boolean;
      notfallscheinbeteiligungRate?: number;
      notes?: string;
    }) => {
      const nextInitialData: NewServiceInitialData = {
        draftId: draft.draftId as Id<'providerServiceDrafts'>,
        sourceKey: draft.sourceKey,
        hasNotfallscheinbeteiligung: draft.hasNotfallscheinbeteiligung,
      };

      if (draft.serviceType) nextInitialData.serviceType = draft.serviceType;
      if (draft.startDate) nextInitialData.startDate = draft.startDate;
      if (draft.endDate) nextInitialData.endDate = draft.endDate;
      if (draft.compensationType) {
        nextInitialData.compensationType = draft.compensationType;
      }
      if (draft.serviceAreaId) nextInitialData.serviceAreaId = draft.serviceAreaId;
      if (draft.manualServiceArea) {
        nextInitialData.manualServiceArea = draft.manualServiceArea;
      }
      if (draft.manualBereitschaftspraxis) {
        nextInitialData.manualBereitschaftspraxis = draft.manualBereitschaftspraxis;
      }
      if (draft.manualFahrdienstgebiet) {
        nextInitialData.manualFahrdienstgebiet = draft.manualFahrdienstgebiet;
      }
      if (draft.fixedPrice !== undefined) {
        nextInitialData.fixedPrice = draft.fixedPrice;
      }
      if (draft.minPrice !== undefined) nextInitialData.minPrice = draft.minPrice;
      if (draft.maxPrice !== undefined) nextInitialData.maxPrice = draft.maxPrice;
      if (draft.notfallscheinbeteiligungRate !== undefined) {
        nextInitialData.notfallscheinbeteiligungRate = draft.notfallscheinbeteiligungRate;
      }
      if (draft.notes) nextInitialData.notes = draft.notes;

      setInitialServiceData(nextInitialData);
      setOpenNew(true);
    },
    []
  );

  const submitNew = async (payload: any) => {
    const analyticsPayload = {
      service_type: payload.serviceType,
      compensation_type: payload.priceMode === 'range' ? 'range' : 'fixed',
      duration_bucket: getDurationBucket(payload.startDate, payload.endDate),
      source: 'provider_dashboard' as const,
    };

    try {
      const dutyId = await createDuty({
        title: payload.title,
        description: payload.description,
        privateNotes: payload.privateNotes,
        startDate: payload.startDate,
        endDate: payload.endDate,
        serviceType: payload.serviceType,
        location: payload.location,
        compensationType: payload.priceMode === 'range' ? 'range' : 'fixed',
        minPrice: payload.priceMode === 'range' ? Number(payload.minPrice) : Number(payload.hourly ?? payload.compensation),
        businessLocationId: payload.businessLocationId,
        ...(payload.priceMode === 'range' && payload.maxPrice ? { maxPrice: Number(payload.maxPrice) } : {}),
        ...(payload.doctorName ? { doctorName: payload.doctorName } : {}),
        ...(payload.serviceAreaId ? { serviceAreaId: payload.serviceAreaId } : {}),
        ...(payload.manualServiceArea ? { manualServiceArea: payload.manualServiceArea } : {}),
        ...(payload.manualBereitschaftspraxis ? { manualBereitschaftspraxis: payload.manualBereitschaftspraxis } : {}),
        ...(payload.manualFahrdienstgebiet ? { manualFahrdienstgebiet: payload.manualFahrdienstgebiet } : {}),
        ...(payload.bereitschaftspraxisId ? { bereitschaftspraxisId: payload.bereitschaftspraxisId } : {}),
        ...(payload.fahrdienstgebietId ? { fahrdienstgebietId: payload.fahrdienstgebietId } : {}),
        ...(payload.hasNotfallscheinbeteiligung ? { hasNotfallscheinbeteiligung: payload.hasNotfallscheinbeteiligung } : {}),
        ...(payload.notfallscheinbeteiligungRate ? { notfallscheinbeteiligungRate: payload.notfallscheinbeteiligungRate } : {}),
      });

      if (initialServiceData?.draftId) {
        await consumeProviderServiceDraft({
          draftId: initialServiceData.draftId,
          dutyId,
        });
      }

      trackServiceCreated(String(dutyId), analyticsPayload);
      setInitialServiceData(undefined);
    } catch (error) {
      trackServiceCreationFailed(error, analyticsPayload);
      throw error;
    }
  };

  const getDisplayName = () => {
    if (me?.title && me?.lastName) {
      return `${me.title} ${me.lastName}`;
    }
    if (me?.lastName) {
      return `${me.firstName || ''} ${me.lastName}`.trim();
    }
    return '';
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ComponentType<{ className?: string }> }> = {
      pending_review: { label: t('provider.status.pendingReview'), variant: 'secondary', icon: Icons.Clock },
      open: { label: t('provider.status.open'), variant: 'default', icon: Icons.Circle },
      filled: { label: t('provider.status.filled'), variant: 'outline', icon: Icons.CheckCircle },
      performed: { label: t('provider.status.performed'), variant: 'outline', icon: Icons.CheckCircle },
      completed: { label: t('provider.status.completed'), variant: 'default', icon: Icons.CheckCircle },
      cancelled: { label: t('provider.status.cancelled'), variant: 'destructive', icon: Icons.XCircle },
    };
    const config = statusMap[status] || { label: status, variant: 'secondary' as const, icon: Icons.Circle };
    const StatusIcon = config.icon;
    return (
      <Badge variant={config.variant} className='gap-1'>
        <StatusIcon className='h-3 w-3' />
        {config.label}
      </Badge>
    );
  };

  /** Determine urgency level for a duty based on proximity */
  const getUrgencyLevel = (startDate: number): 'imminent' | 'soon' | 'normal' => {
    const diff = startDate - now;
    if (diff <= IMMINENT_THRESHOLD_MS) return 'imminent';
    if (diff <= URGENT_THRESHOLD_MS) return 'soon';
    return 'normal';
  };

  const isLoading = stats === undefined || me === undefined;

  // Compute action summary values
  const openCount = stats?.openDuties ?? 0;
  const applicationCount = stats?.pendingApplications ?? 0;
  const activeCount = stats?.activeDuties ?? 0;
  const hasActions = applicationCount > 0;

  return (
    <div className='bg-background'>
      {/* Header — clean, functional, no decorative noise */}
      <div className='border-b border-border'>
        <div className='container mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6'>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
            {/* Left: Name + summary */}
            <div className='space-y-1'>
              {me === undefined ? (
                <>
                  <Skeleton className='h-7 w-48' />
                  <Skeleton className='h-4 w-64' />
                </>
              ) : (
                <>
                  <h1 className='text-lg sm:text-xl font-semibold text-foreground tracking-tight'>{getDisplayName() || t('provider.welcome')}</h1>
                  {/* Inline status summary */}
                  {!isLoading && (
                    <p className='text-sm text-muted-foreground'>
                      {openCount > 0 && (
                        <span>
                          {t('provider.stats.open')}: <span className='font-medium text-foreground'>{openCount}</span>
                        </span>
                      )}
                      {openCount > 0 && activeCount > 0 && <span className='mx-1.5 text-muted-foreground/30'>&middot;</span>}
                      {activeCount > 0 && (
                        <span>
                          {t('provider.stats.active')}: <span className='font-medium text-foreground'>{activeCount}</span>
                        </span>
                      )}
                      {openCount === 0 && activeCount === 0 && <span>{t('provider.subtitle')}</span>}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Right: Primary action only */}
            <Button onClick={() => setOpenNew(true)} size='sm' className='gap-1.5'>
              <Icons.Plus className='h-4 w-4' />
              {t('provider.createService')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='container mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6 lg:py-8'>
        {/* DA-438: Profile Completion Banner */}
        <ProfileCompletionBanner />

        {highlightedDraft && (
          <section className={cn('mb-6 rounded-2xl border p-5 sm:p-6', highlightedDraftId ? 'border-primary/40 bg-primary/5' : 'border-border bg-card')}>
            <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
              <div className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <Badge variant='secondary'>Website-Entwurf</Badge>
                  {highlightedDraftId && <Badge variant='default'>Neu gespeichert</Badge>}
                </div>
                <div>
                  <h2 className='text-base font-semibold text-foreground'>Gespeicherter Dienstentwurf bereit zum Fertigstellen</h2>
                  <p className='text-sm text-muted-foreground'>Ihre Angaben aus dem anonymen Website-Formular wurden gespeichert. Ergänzen Sie jetzt nur noch die anbieterspezifischen Pflichtfelder und veröffentlichen Sie den Dienst.</p>
                </div>
                <dl className='grid gap-2 text-sm text-muted-foreground sm:grid-cols-2'>
                  <div>
                    <dt className='font-medium text-foreground'>Dienst</dt>
                    <dd>{highlightedDraft.serviceType}</dd>
                  </div>
                  <div>
                    <dt className='font-medium text-foreground'>Zeitraum</dt>
                    <dd>
                      {dateTimeFormatter.format(new Date(highlightedDraft.startDate))} - {dateTimeFormatter.format(new Date(highlightedDraft.endDate))}
                    </dd>
                  </div>
                  {(highlightedDraft.serviceAreaName || highlightedDraft.manualServiceArea || highlightedDraft.kvRegion) && (
                    <div>
                      <dt className='font-medium text-foreground'>Region</dt>
                      <dd>{highlightedDraft.serviceAreaName || highlightedDraft.manualServiceArea || highlightedDraft.kvRegion}</dd>
                    </div>
                  )}
                  <div>
                    <dt className='font-medium text-foreground'>Vergütung</dt>
                    <dd>{highlightedDraft.compensationType === 'range' ? `${highlightedDraft.minPrice ?? '–'} € - ${highlightedDraft.maxPrice ?? '–'} € / Std.` : `${highlightedDraft.fixedPrice ?? '–'} € / Std.`}</dd>
                  </div>
                </dl>
              </div>

              <Button onClick={() => openDraftInModal(highlightedDraft)} className='gap-1.5'>
                <Icons.ChevronRight className='h-4 w-4' />
                Entwurf fortsetzen
              </Button>
            </div>
          </section>
        )}

        {/* Action required callout — only when there are pending applications */}
        {hasActions && !isLoading && (
          <Link to='/provider/services' className='block mb-6 group'>
            <div className='flex items-center gap-3 px-4 py-3 rounded-lg border border-primary/20 bg-primary/5 transition-colors hover:bg-primary/10'>
              <div className='flex items-center justify-center size-8 rounded-md bg-primary/10'>
                <Icons.Users className='size-4 text-primary' />
              </div>
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-medium text-foreground'>{t('provider.urgentTasks.newApplications', { count: applicationCount })}</p>
              </div>
              <Icons.ChevronRight className='size-4 text-muted-foreground group-hover:text-primary transition-colors' />
            </div>
          </Link>
        )}

        {/* Upcoming duties — full width, with urgency hierarchy */}
        <section className='mb-8'>
          <div className='flex items-center justify-between mb-4'>
            <h2 className='text-base font-semibold text-foreground'>{t('provider.upcomingDuties')}</h2>
            <Button variant='ghost' size='sm' render={<Link to='/provider/services' />} className='gap-1 -mr-2 text-muted-foreground hover:text-foreground'>
              {t('provider.viewAll')}
              <Icons.ChevronRight className='h-4 w-4' />
            </Button>
          </div>

          {isLoading ? (
            <div className='space-y-2'>
              {[0, 1, 2].map((i) => (
                <div key={i} className='flex items-center gap-4 p-4 rounded-lg border'>
                  <Skeleton className={cn('rounded-md flex-shrink-0', i === 0 ? 'w-14 h-14' : 'w-11 h-11')} />
                  <div className='flex-1 space-y-2'>
                    <Skeleton className='h-4 w-3/5' />
                    <Skeleton className='h-3 w-2/5' />
                  </div>
                </div>
              ))}
            </div>
          ) : upcoming.length > 0 ? (
            <div className='space-y-2'>
              {upcoming.map((d, index) => {
                const urgency = getUrgencyLevel(d.startDate);
                const isFirst = index === 0;
                const isToday = isBerlinToday(d.startDate);
                const startTime = formatBerlinTime(d.startDate, locale);
                const endTime = formatBerlinTime(d.endDate, locale);
                const weekday = formatBerlinDateWithOptions(d.startDate, { weekday: 'short' }, locale);

                return (
                  <Link key={String(d._id)} to='/provider/service/$id' params={{ id: String(d._id) }} className='block group'>
                    <div
                      className={cn(
                        'flex items-center gap-3 sm:gap-4 rounded-lg border transition-colors',
                        'hover:border-primary/30 focus-visible:ring-2 focus-visible:ring-ring',
                        // First item is larger/more prominent
                        isFirst ? 'p-4 sm:p-5' : 'p-3 sm:p-4',
                        // Urgency-based emphasis: full tinted border (urgency is also labelled below, never colour alone)
                        urgency === 'imminent' && 'border-destructive/30 bg-destructive/[0.03]',
                        urgency === 'soon' && 'border-warning/40 bg-warning/[0.02]',
                        urgency === 'normal' && 'bg-card'
                      )}
                    >
                      {/* Date column */}
                      <div
                        className={cn(
                          'flex flex-col items-center justify-center rounded-md flex-shrink-0',
                          isFirst ? 'w-14 h-14 sm:w-16 sm:h-16' : 'w-11 h-11 sm:w-12 sm:h-12',
                          urgency === 'imminent' ? 'bg-destructive/10 text-destructive' : 'bg-muted text-foreground'
                        )}
                      >
                        <span className='text-xs font-medium uppercase leading-none'>{formatBerlinDateWithOptions(d.startDate, { month: 'short' }, locale)}</span>
                        <span className={cn('font-bold leading-none', isFirst ? 'text-xl sm:text-2xl' : 'text-base sm:text-lg')}>{getBerlinDateParts(d.startDate).day}</span>
                      </div>

                      {/* Content */}
                      <div className='flex-1 min-w-0'>
                        <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-2'>
                          <h3 className={cn('font-semibold text-foreground truncate', isFirst ? 'text-base' : 'text-sm')}>{d.title}</h3>
                          <div className='flex items-center gap-2 flex-shrink-0'>
                            {isToday ? (
                              <Badge variant={urgency === 'imminent' ? 'destructive' : 'secondary'} className='text-xs px-1.5 py-0'>
                                {t('dates.today')}
                              </Badge>
                            ) : urgency === 'imminent' ? (
                              <Badge variant='destructive' className='text-xs px-1.5 py-0'>
                                Dringend
                              </Badge>
                            ) : urgency === 'soon' ? (
                              <Badge variant='secondary' className='text-xs px-1.5 py-0'>
                                Bald
                              </Badge>
                            ) : null}
                            {getStatusBadge(d.status)}
                          </div>
                        </div>
                        <div className='flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground'>
                          <span className='flex items-center gap-1'>
                            <Icons.MapPin className='h-3 w-3' />
                            {d.location?.city || '—'}
                          </span>
                          <span className='flex items-center gap-1'>
                            <Icons.Clock className='h-3 w-3' />
                            {weekday}, {startTime}–{endTime}
                          </span>
                          {d.minPrice !== undefined && <span className='font-medium text-foreground'>{d.compensationType === 'range' && d.maxPrice !== undefined ? `${d.minPrice}–${d.maxPrice}` : `${d.minPrice}`}€/h</span>}
                        </div>
                      </div>

                      <Icons.ChevronRight className='h-4 w-4 text-muted-foreground/50 group-hover:text-primary transition-colors flex-shrink-0' />
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className='rounded-lg border border-dashed border-border py-10 sm:py-14 text-center'>
              <p className='text-sm font-medium text-foreground mb-1'>{t('provider.noUpcoming')}</p>
              <p className='text-xs text-muted-foreground mb-4 max-w-xs mx-auto'>{t('provider.noUpcomingHint')}</p>
              <Button onClick={() => setOpenNew(true)} variant='outline' size='sm' className='gap-1.5'>
                <Icons.Plus className='h-4 w-4' />
                {t('provider.createService')}
              </Button>
            </div>
          )}
        </section>

        {/* Activity feed — full width, secondary importance */}
        <section>
          <ActivityFeed activities={activities || []} isLoading={activities === undefined} />
        </section>
      </div>

      {/* New Service Modal */}
      <NewServiceModal open={openNew} onOpenChange={handleModalOpenChange} onSubmit={submitNew} initialData={initialServiceData} />
    </div>
  );
};

export const Route = createFileRoute('/_protected/provider/dashboard')({
  validateSearch: (search: Record<string, unknown>) => ({
    draftId: typeof search['draftId'] === 'string' ? search['draftId'] : undefined,
  }),
  component: ProviderDashboardPage,
});
